/* eslint-disable no-restricted-syntax -- The admin's own design system (shared/rd), ported from the Restaurant Dashboard design; admin chose app-local components over @a2z/ui-v2 (2026-10-26). See apps/admin/AGENTS.md. */
import type { CSSProperties, ReactNode } from "react";
import { Illos } from "./illustrations";
import { cx } from "./cx";
import { Button } from "./controls";
import { Banner } from "./feedback";

export type BlobTone = "green" | "yellow" | "blue" | "purple" | "red";

/** A hand-drawn spot illustration from the canvas set ("admin-empty", "offline"…). */
export function Illustration({ id, size, className }: { id: string; size: number; className?: string }) {
  const entry = Illos.get(id);
  if (!entry) return null;
  return (
    <span
      className={cx("rd-illo", className)}
      style={{ width: size, height: size }}
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: Illos.source(entry.svg) }}
    />
  );
}

/** One soft morphing shape behind content (the design's page blobs). */
function Blob({ tone, style }: { tone: BlobTone; style: CSSProperties }) {
  return <div className="rd-blob" aria-hidden="true" style={{ background: `var(--rd-blob-${tone})`, ...style }} />;
}

/** Up to three blobs behind a page; the host needs `rd-blob-host`. */
export function PageBlobs({ tones }: { tones: BlobTone[] }) {
  const [a, b, c] = tones;
  return (
    <div className="rd-blobs" aria-hidden="true">
      {a ? <div className="rd-blob rd-blob-a" style={{ background: `var(--rd-blob-${a})` }} /> : null}
      {b ? <div className="rd-blob rd-blob-b" style={{ background: `var(--rd-blob-${b})` }} /> : null}
      {c ? <div className="rd-blob rd-blob-c" style={{ background: `var(--rd-blob-${c})` }} /> : null}
    </div>
  );
}

const ALERT_TONE = { error: "error", info: "info", warn: "warning", success: "success" } as const;

/** An inline message (a failed save, a notice): the design's Banner, by its older name. */
export function Alert({
  tone = "error",
  title,
  action,
  children,
  className,
}: {
  tone?: keyof typeof ALERT_TONE;
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Banner tone={ALERT_TONE[tone]} title={title} actions={action} className={className}>
      {children}
    </Banner>
  );
}

/**
 * Loading: the illustrated "Loading …" card over skeleton rows, as on the
 * canvas's Loading board. `rows={0}` drops the skeleton (a drawer, a card).
 */
export function LoadingState({ title, sub, rows = 6 }: { title: string; sub?: string; rows?: number }) {
  const widths = ["42%", "30%", "36%", "26%", "40%", "32%"];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }} role="status" aria-live="polite" aria-busy="true">
      <div className="rd-card" style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 18px" }}>
        <Illustration id="admin-loading" size={88} />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{title}</span>
          {sub ? <span style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>{sub}</span> : null}
        </div>
        <div style={{ display: "flex", gap: 6 }} aria-hidden="true">
          {[0, 0.15, 0.3].map((delay) => (
            <span key={delay} className="rd-loading-dot" style={{ animationDelay: `${delay}s` }} />
          ))}
        </div>
      </div>
      {rows > 0 ? (
        <div className="rd-card" style={{ overflow: "hidden" }} aria-hidden="true">
          <div style={{ height: 40, background: "var(--rd-surface-soft)", borderBottom: "1px solid var(--rd-line-header)" }} />
          {Array.from({ length: rows }, (_, i) => (
            <div
              key={i}
              style={{ display: "flex", alignItems: "center", gap: 14, height: 48, padding: "0 16px", borderBottom: "1px solid var(--rd-line-row)" }}
            >
              <span className="rd-skel" style={{ width: 28, height: 28, borderRadius: 14, animationDelay: `${i / 10}s` }} />
              <span className="rd-skel" style={{ width: widths[i % widths.length], height: 12, animationDelay: `${i / 10}s` }} />
              <span style={{ flex: 1 }} />
              <span className="rd-skel" style={{ width: 70, height: 12, animationDelay: `${i / 10}s` }} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Nothing to show: an illustration on a soft blob, what is missing, and the one next step. */
export function EmptyView({
  title,
  sub,
  action,
  illustration = "admin-empty",
  tone = "yellow",
}: {
  title: ReactNode;
  sub?: ReactNode;
  action?: ReactNode;
  illustration?: string;
  tone?: BlobTone;
}) {
  return (
    <div className="rd-state">
      <div style={{ position: "relative", width: 140, height: 140, display: "flex", marginBottom: 6 }}>
        <Blob tone={tone} style={{ inset: "-6% -14%", animation: "rd-morph2 12s ease-in-out infinite" }} />
        <span style={{ position: "relative", display: "flex" }}>
          <Illustration id={illustration} size={140} />
        </span>
      </div>
      <span className="rd-state-title">{title}</span>
      {sub ? <span className="rd-state-sub">{sub}</span> : null}
      {action ? <div style={{ marginTop: 6 }}>{action}</div> : null}
    </div>
  );
}

/**
 * Something failed to load: says what, and offers the retry. A failed load
 * never reads as "nothing here" — that is EmptyView's job.
 */
export function ErrorState({
  title,
  sub,
  onRetry,
  retryLabel,
  retrying,
}: {
  title: ReactNode;
  sub?: ReactNode;
  onRetry?: () => void;
  retryLabel: string;
  retrying?: boolean;
}) {
  return (
    <div className="rd-state" role="alert">
      <div style={{ position: "relative", width: 140, height: 140, display: "flex", marginBottom: 6 }}>
        <Blob tone="red" style={{ inset: "-6% -14%", animation: "rd-morph2 12s ease-in-out infinite" }} />
        <span style={{ position: "relative", display: "flex" }}>
          <Illustration id="admin-alerts" size={140} />
        </span>
      </div>
      <span className="rd-state-title">{title}</span>
      {sub ? <span className="rd-state-sub">{sub}</span> : null}
      {onRetry ? (
        <div style={{ marginTop: 6 }}>
          <Button size="sm" onClick={onRetry} disabled={retrying}>
            {retryLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** A whole-page state (offline, not found): big illustration, title, actions, on a blob. */
export function PageState({
  illustration,
  tone,
  eyebrow,
  pill,
  title,
  sub,
  actions,
  footnote,
}: {
  illustration: string;
  tone: BlobTone;
  /** Small coloured label over the title ("404"). */
  eyebrow?: ReactNode;
  /** A pulsing status pill ("Offline · reconnecting"). */
  pill?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  footnote?: ReactNode;
}) {
  return (
    <div className="rd-state rd-state-page rd-blob-host">
      <div className="rd-blobs" aria-hidden="true">
        <div className="rd-blob rd-blob-a" style={{ background: `var(--rd-blob-${tone})` }} />
      </div>
      <Illustration id={illustration} size={220} />
      {pill ? (
        <span className="rd-state-pill">
          <span />
          <span>{pill}</span>
        </span>
      ) : null}
      {eyebrow ? <span className="rd-state-eyebrow">{eyebrow}</span> : null}
      <span className="rd-state-title">{title}</span>
      {sub ? <span className="rd-state-sub">{sub}</span> : null}
      {actions ? <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center", marginTop: 6 }}>{actions}</div> : null}
      {footnote ? <span style={{ fontSize: 12, color: "var(--rd-text-meta)" }}>{footnote}</span> : null}
    </div>
  );
}

/**
 * Inside a table's frame when it has no rows (the canvas's Empty table
 * boards): illustration, what is missing, why, and the actions. Two cases:
 * first use — "admin-empty-table" with the create action; no results —
 * "admin-no-results" naming the search/filters with "Clear filters".
 */
export function TableEmpty({
  illustration = "admin-empty-table",
  title,
  children,
  actions,
}: {
  illustration?: "admin-empty-table" | "admin-no-results" | (string & {});
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="rd-tempty" role="status">
      <div className="rd-tempty-art">
        <Illustration id={illustration} size={120} />
      </div>
      <span className="rd-tempty-title">{title}</span>
      {children ? <span className="rd-tempty-text">{children}</span> : null}
      {actions ? <div className="rd-tempty-actions">{actions}</div> : null}
    </div>
  );
}
